import React, { useEffect, useState, useRef } from "react";
import Scrollbars from "react-custom-scrollbars-2";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import { SidebarData } from "./sidebardata";
import Chatbot from '../Bot/Chatbot';
import { ChevronLeft, User, Grid, Layers, Bookmark, RefreshCw, Calendar, Clock, Activity, Sun, DollarSign, Camera, MapPin, Layout, Box, Briefcase, Bell, FileText, Users, Award, TrendingUp, FileMinus, FilePlus, List, Settings, Clipboard, BarChart2, Home, BookOpen, Moon, HelpCircle, LogIn, LogOut, CheckCircle, MoreHorizontal, ChevronDown } from 'react-feather';

const Sidebar = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const { t } = useTranslation();

  // Keep the StayX two-column rail physically constrained to the 75px design
  // width. Bootstrap .nav-link rules can otherwise stretch active items into
  // the secondary navigation pane.
  const railNavStyle = { width: 75, minWidth: 75, maxWidth: 75, alignItems: "stretch" };
  const railLinkStyle = { width: 75, minWidth: 75, maxWidth: 75, boxSizing: "border-box" };
  const railIconProps = { size: 20, strokeWidth: 1.8 };

  const [sidebarData, setSidebarData] = useState(SidebarData);
  const [isSideMenu, setIsSideMenu] = useState("employee-dashboard");
  const profileTabRef = useRef(null);
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  const [isStayXMoreOpen, setIsStayXMoreOpen] = useState(false);
  const stayXMoreRef = useRef(null);
  const stayXMoreDropdownRef = useRef(null);
  const [stayXMorePosition, setStayXMorePosition] = useState({ top: 0, left: 0 });

  const toggleChatbot = (e) => {
    setIsChatbotOpen((prev) => !prev);
  };

  useEffect(() => {
    if (profileTabRef.current) {
      profileTabRef.current.click();
    }
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const clickedTrigger = stayXMoreRef.current?.contains(event.target);
      const clickedDropdown = stayXMoreDropdownRef.current?.contains(event.target);

      if (!clickedTrigger && !clickedDropdown) {
        setIsStayXMoreOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    setIsStayXMoreOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!isStayXMoreOpen) return undefined;

    const updateStayXMorePosition = () => {
      const trigger = stayXMoreRef.current;
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      const menuWidth = 286;
      const viewportPadding = 12;
      const preferredLeft = rect.right - menuWidth;
      const maxLeft = Math.max(
        viewportPadding,
        window.innerWidth - menuWidth - viewportPadding
      );

      setStayXMorePosition({
        top: rect.bottom + 8,
        left: Math.min(Math.max(viewportPadding, preferredLeft), maxLeft),
      });
    };

    updateStayXMorePosition();
    window.addEventListener("resize", updateStayXMorePosition);
    window.addEventListener("scroll", updateStayXMorePosition, true);

    return () => {
      window.removeEventListener("resize", updateStayXMorePosition);
      window.removeEventListener("scroll", updateStayXMorePosition, true);
    };
  }, [isStayXMoreOpen]);

  useEffect(() => {
    const menuMapping = {
      '/hospitality': 'employee',
      '/departments': 'employee',
      '/designations': 'employee',
      '/profile': 'profile',
      '/leave-types': 'employee',
      '/holidays': 'employee',
      '/shift-list': 'employee',
      '/overtime': 'employee',
      '/announcements': 'employee',
      '/leaves-employee': 'leaves',
      '/adminleaves': 'leaves',
      '/leave-applications': 'leaves',
      '/leaves-balance': 'leaves',
      '/leave-settings': 'leaves',
      '/attendance-weekly': 'attendance',
      '/attendance-daily': 'attendance',
      '/adminattendance': 'attendance',
      '/attendance-employee': 'attendance',
      '/payroll-overview': 'payroll-overview',
      '/salary': 'payroll-overview',
      '/payroll-items': 'payroll-overview',
      '/run-payroll': 'payroll-overview',
      '/payroll-settings': 'payroll-overview',
      '/payroll-history': 'payroll-overview',
      '/payroll-reports': 'payroll-overview',
      '/devices': 'devices',
      '/deviceslog': 'devices',
      '/device-users': 'devices',
      '/manage-locations': 'company-settings',
      '/reports': 'reports',
      '/property-configuration': 'property-configuration',
    };

    const matchedMenu = Object.keys(menuMapping)
      .sort((a, b) => b.length - a.length)
      .find(route => pathname.startsWith(route)) || "";

    if (matchedMenu) {
      toggleSidebar(menuMapping[matchedMenu]);
    } else {
      toggleSidebar('dashboard');
    }
  }, [pathname]);

  useEffect(() => {
    if (!isSideMenu) {
      toggleSidebar('employee-dashboard');
    }
  }, []);

  const expandSubMenus = (menu) => {
    const updatedAdminSidebar = sidebarData.map((section) => {
      const updatedSection = { ...section };
      updatedSection.menu = section.menu.map((menuItem) =>
        menu.menuValue !== menuItem.menuValue
          ? { ...menuItem, showSubRoute: false }
          : { ...menuItem, showSubRoute: true }
      );
      return updatedSection;
    });
    setSidebarData(updatedAdminSidebar);
  };

  const toggleSidebar = (value) => {
    setIsSideMenu(value);
  };

  const normalizedPath = pathname.replace(/\/+$/, "") || "/";

  const normalizeRoute = (route) =>
    String(route || "").replace(/\/+$/, "") || "/";

  const isActive = (routes) => {
    const routeArray = Array.isArray(routes) ? routes : [routes];
    return routeArray.some((route) => normalizedPath === normalizeRoute(route));
  };

  const isSectionActive = (routes) => {
    const routeArray = Array.isArray(routes) ? routes : [routes];
    return routeArray.some((route) => {
      const base = normalizeRoute(route);
      return normalizedPath === base || normalizedPath.startsWith(`${base}/`);
    });
  };

  return (
    <>
      <div className="sidebar">
        <div className="sidebar-inner slimscroll-" style={{ overflow: 'auto' }}>
          <div id="sidebar-menu" className="sidebar-menu">
            <nav className="greedys sidebar-horizantal" id="horizantal-sidebar">
              {/* Horizontal Sidebar content */}
            </nav>

            <Scrollbars
              autoHide={false}
              autoHideTimeout={1000}
              autoHideDuration={200}
              autoHeight
              autoHeightMin={0}
              autoHeightMax="100vh"
              thumbMinSize={30}
              universal={false}
              hideTracksWhenNotNeeded={true}
            >
              <ul className="sidebar-vertical" id="veritical-sidebar">
                {sidebarData.map((mainTittle, index) => (
                  <React.Fragment key={index + 1}>
                    <li className="menu-title">
                      <span>{t(mainTittle.tittle)}</span>
                    </li>
                    {mainTittle.menu.map((menu, menuIndex) => (
                      <React.Fragment key={menuIndex + 1}>
                        {!menu.hasSubRoute ? (
                          <li>
                            <Link
                              to={menu.route}
                              className={isActive([menu.route]) ? "active" : ""}
                            >
                              <i className={menu?.icon} />
                              <span>{t(menu.menuValue)}</span>
                            </Link>
                          </li>
                        ) : (
                          <li className="submenu">
                            <Link
                              to="#"
                              onClick={() => expandSubMenus(menu)}
                              className={menu.showSubRoute ? "subdrop" : ""}
                            >
                              <i className={menu?.icon} />
                              <span>{t(menu.menuValue)}</span>
                              <span className="menu-arrow" />
                            </Link>
                            <ul style={{ display: menu.showSubRoute ? "block" : "none" }}>
                              {menu.subMenus.map((subMenus, subMenu) => (
                                <React.Fragment key={subMenu + 1}>
                                  {subMenus?.showMenuRoute ? (
                                    <li>
                                      <Link
                                        to={subMenus.route}
                                        className={isActive([subMenus.route]) ? "active" : ""}
                                      >
                                        {t(subMenus.menuValue)}
                                        <span className="menu-arrow" />
                                      </Link>
                                      <ul style={{ display: subMenus.showSubRoute ? "block" : "none" }}>
                                        {subMenus?.subMenusValues?.map((value, index) => (
                                          <li key={index}>
                                            <Link
                                              to={value.route}
                                              className={isActive([value.route]) ? "active" : ""}
                                            >
                                              <span>{t(value.menuValue)}</span>
                                            </Link>
                                          </li>
                                        ))}
                                      </ul>
                                    </li>
                                  ) : (
                                    <li>
                                      <Link
                                        to={subMenus.route}
                                        className={isActive([subMenus.route]) ? "active" : ""}
                                      >
                                        {t(subMenus.menuValue)}
                                      </Link>
                                      <ul>
                                        {subMenus?.subMenusValues?.map((value, index) => (
                                          <li key={index}>
                                            <Link
                                              to={value.route}
                                              className={isActive([value.route]) ? "active" : ""}
                                            >
                                              <span>{t(value.menuValue)}</span>
                                            </Link>
                                          </li>
                                        ))}
                                      </ul>
                                    </li>
                                  )}
                                </React.Fragment>
                              ))}
                            </ul>
                          </li>
                        )}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
              </ul>
            </Scrollbars>
          </div>
        </div>

        <div className="two-col-bar" id="two-col-bar">
          <div className="sidebar sidebar-twocol">
            <div className="sidebar-left slimscroll">
              <div
                className="nav flex-column nav-pills"
                id="v-pills-tab"
                role="tablist"
                aria-orientation="vertical"
                style={railNavStyle}
              >
                <Link
                  className={`nav-link ${isActive(["/hospitality/"]) ? "active" : ""}`}
                  title={t("Dashboard")}
                  onClick={() => toggleSidebar("dashboard")}
                  to="/hospitality/"
                  style={railLinkStyle}
                >
                  <Home {...railIconProps} />
                  <small>{t("Home")}</small>
                </Link>
                <Link
                  className={`nav-link ${isActive([
                    "/hospitality/property-configuration",
                    "/departments",
                    "/designations",
                    "/profile",
                    "/leave-types",
                    "/holiday",
                    "/shift-list",
                    "/overtime",
                  ]) ? "active" : ""}`}
                  title={t("My Property")}
                  onClick={() => toggleSidebar("/hospitality/property-configuration")}
                  to="/hospitality/property-configuration"
                  style={railLinkStyle}
                >
                  <Briefcase {...railIconProps} />
                  <small>{t("Property")}</small>
                </Link>
                <Link
                  className={`nav-link ${isActive([
                    "/hospitality/reservation-list",
                    "/hospitality/guest-reservation",
                  ]) ? "active" : ""}`}
                  title={t("Bookings")}
                  onClick={() => toggleSidebar("employee")}
                  to="/hospitality/reservation-list"
                  style={railLinkStyle}
                >
                  <BookOpen {...railIconProps} />
                  <small>{t("Bookings")}</small>
                </Link>
                <Link
                  className={`nav-link ${isActive(["/hospitality/refunds"]) ? "active" : ""}`}
                  title={t("Refunds")}
                  onClick={() => toggleSidebar("employee")}
                  to="/hospitality/refunds"
                  style={railLinkStyle}
                >
                  <DollarSign {...railIconProps} />
                  <small>{t("Refunds")}</small>
                </Link>
                <Link
                  className={`nav-link ${isActive([
                    "/hospitality/inhouse-guest",
                    "/hospitality/guest",
                  ]) ? "active" : ""}`}
                  title={t("In House Guest")}
                  onClick={() => toggleSidebar("/hospitality/inhouse-guest")}
                  to="/hospitality/inhouse-guest"
                  style={railLinkStyle}
                >
                  <User {...railIconProps} />
                  <small>{t("In House")}</small>
                </Link>
                <Link
                  className={`nav-link ${isActive([
                    "/hospitality/day-invoices",
                    "/deviceslogs",
                    "/users",
                  ]) ? "active" : ""}`}
                  title={t("Invoices")}
                  onClick={() => toggleSidebar("/hospitality/day-invoices")}
                  to="/hospitality/day-invoices"
                  style={railLinkStyle}
                >
                  <FileText {...railIconProps} />
                  <small>{t("Invoices")}</small>
                </Link>
                <Link
                  className={`nav-link ${isActive(["/hospitality/day-end"]) ? "active" : ""}`}
                  title={t("Day End")}
                  onClick={() => toggleSidebar("/hospitality/day-end")}
                  to="/hospitality/day-end"
                  style={railLinkStyle}
                >
                  <Moon {...railIconProps} />
                  <small>{t("Day End")}</small>
                </Link>
                <Link
                  className={`nav-link ${isActive(["/hospitality/day-book"]) ? "active" : ""}`}
                  title={t("Day Book")}
                  onClick={() => toggleSidebar("/hospitality/day-book")}
                  to="/hospitality/day-book"
                  style={railLinkStyle}
                >
                  <Clipboard {...railIconProps} />
                  <small>{t("Day Book")}</small>
                </Link>
                <Link
                  className={`nav-link ${isActive([
                    "/hospitality/reports",
                    "/hospitality/occupancy-report",
                    "/hospitality/reservation-report",
                    "/hospitality/room-status-report",
                    "/hospitality/daily-room-sold",
                    "/hospitality/shift-end-summary",
                    "/hospitality/ota-booking-volume",
                    "/hospitality/booking-source-performance",
                    "/hospitality/daily-revenue-analysis",
                    "/hospitality/payment-mode-report/",
                    "/hospitality/sales-performance/",
                    "/hospitality/front-office-billings/",
                    "/hospitality/ota-booking-revenue/",
                    "/hospitality/roomno-wise-sales",
                    "hospitality/daily-flash-report"
                  ]) ? "active" : ""}`}
                  title={t("Reports")}
                  onClick={() => toggleSidebar("/hospitality/reports")}
                  to="/hospitality/reports"
                  style={railLinkStyle}
                >
                  <BarChart2 {...railIconProps} />
                  <small>{t("Reports")}</small>
                </Link>
                <button
                  className={`nav-link ${isChatbotOpen ? "active" : ""}`}
                  title={t("Support")}
                  onClick={toggleChatbot}
                  type="button"
                  style={railLinkStyle}
                >
                  <HelpCircle {...railIconProps} />
                  <small>{t("Support")}</small>
                </button>
                <Link
                  className={`nav-link ${isActive(["/logout"]) ? "active" : ""}`}
                  title={t("Logout")}
                  onClick={() => toggleSidebar("/logout")}
                  to="/logout"
                  style={railLinkStyle}
                >
                  <LogOut {...railIconProps} />
                  <small>{t("Logout")}</small>
                </Link>
              </div>
            </div>
            <div className="sidebar-right">
              <div className="tab-content-horizontal" id="v-pills-tabContent">
                {isSideMenu === "dashboard" && (
                  <div className="tab-pane fade active show">
                    <div className="submenu-horizontal">
                      <ul>
                        <li>
                          <Link
                            className={isActive(['/employee-dashboard']) ? "active" : ""}
                            to="/employee-dashboard"
                          >
                            <Home size={16} style={{ marginRight: '4px' }} /> {t("My Dashboard")}
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
                {isSideMenu === "employee" && (
                  <div className="tab-pane fade active show">
                    <style>{`
                      /* StayX signature premium navigation */
                      .sidebar-right,
                      .tab-content-horizontal,
                      .submenu-horizontal {
                        min-width: 0 !important;
                        max-width: 100% !important;
                        overflow-x: hidden !important;
                      }

                      .stayx-premium-topnav {
                        position: relative;
                        width: 100%;
                        max-width: 100%;
                        box-sizing: border-box;
                        margin: 0;
                        padding: 6px 10px;
                        border: 0;
                        border-bottom: 1px solid #d9e2ee;
                        border-radius: 0;
                        background:
                          linear-gradient(180deg, #ffffff 0%, #f7faff 100%);
                        box-shadow:
                          0 6px 18px rgba(15, 23, 42, 0.055),
                          inset 0 -1px 0 rgba(255,255,255,.96);
                        overflow: visible;
                      }

                      .stayx-premium-topnav::before {
                        content: "";
                        position: absolute;
                        left: 10px;
                        right: 10px;
                        top: 0;
                        height: 1px;
                        background: linear-gradient(
                          90deg,
                          transparent,
                          rgba(47,111,237,.16),
                          transparent
                        );
                        pointer-events: none;
                      }

                      .stayx-premium-topnav .cm-channel-topnav-list {
                        display: flex;
                        align-items: center;
                        gap: 4px;
                        width: 100%;
                        max-width: 100%;
                        min-width: 0;
                        min-height: 42px;
                        margin: 0;
                        padding: 0;
                        box-sizing: border-box;
                        overflow: visible;
                      }

                      .stayx-premium-topnav .cm-channel-nav-item {
                        display: flex;
                        align-items: center;
                        min-width: 0;
                        flex: 0 0 auto;
                      }

                      .stayx-premium-topnav .cm-channel-nav-item > a {
                        position: relative;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        gap: 8px;
                        min-height: 38px;
                        padding: 0 11px 0 8px;
                        border: 1px solid #e7ecf3;
                        border-radius: 10px;
                        background: rgba(255,255,255,.76);
                        color: #293b56;
                        font-size: 12.4px;
                        font-weight: 800;
                        letter-spacing: -0.012em;
                        line-height: 1;
                        text-decoration: none;
                        white-space: nowrap;
                        box-shadow:
                          0 1px 2px rgba(15,23,42,.025),
                          inset 0 1px 0 rgba(255,255,255,.96);
                        transition:
                          transform 150ms ease,
                          background-color 150ms ease,
                          border-color 150ms ease,
                          color 150ms ease,
                          box-shadow 150ms ease;
                      }

                      .stayx-premium-topnav .cm-channel-nav-item > a svg {
                        width: 15px;
                        height: 15px;
                        flex: 0 0 15px;
                        color: #46617f;
                        stroke-width: 2;
                        transition:
                          color 150ms ease,
                          transform 150ms ease;
                      }

                      .stayx-premium-topnav .cm-channel-nav-item > a::before {
                        content: "";
                        width: 27px;
                        height: 27px;
                        margin-right: -23px;
                        border-radius: 8px;
                        background: #f3f6fb;
                        border: 1px solid #e7ecf3;
                        flex: 0 0 27px;
                        transition:
                          background-color 150ms ease,
                          border-color 150ms ease;
                      }

                      .stayx-premium-topnav .cm-channel-nav-item > a svg {
                        position: relative;
                        z-index: 1;
                      }

                      .stayx-premium-topnav .cm-channel-nav-item > a:hover {
                        color: #123b72;
                        background: #ffffff;
                        border-color: #cfdced;
                        box-shadow:
                          0 5px 14px rgba(26, 65, 120, 0.08),
                          inset 0 1px 0 rgba(255,255,255,.98);
                        transform: translateY(-1px);
                      }

                      .stayx-premium-topnav .cm-channel-nav-item > a:hover::before {
                        background: #edf4ff;
                        border-color: #c9d9f5;
                      }

                      .stayx-premium-topnav .cm-channel-nav-item > a:hover svg {
                        color: #2563eb;
                        transform: translateY(-0.5px);
                      }

                      .stayx-premium-topnav .cm-channel-nav-item > a.active {
                        color: #0b45a8;
                        background:
                          linear-gradient(180deg, #ffffff 0%, #eef5ff 100%);
                        border-color: #9dbcf6;
                        font-weight: 900;
                        box-shadow:
                          0 6px 16px rgba(37,99,235,.13),
                          inset 0 1px 0 rgba(255,255,255,.98);
                      }

                      .stayx-premium-topnav .cm-channel-nav-item > a.active::before {
                        background:
                          linear-gradient(180deg, #eaf2ff 0%, #dce9ff 100%);
                        border-color: #a9c3f5;
                        box-shadow: inset 0 1px 0 rgba(255,255,255,.75);
                      }

                      .stayx-premium-topnav .cm-channel-nav-item > a.active::after {
                        content: "";
                        position: absolute;
                        left: 10px;
                        right: 10px;
                        bottom: -7px;
                        height: 2px;
                        border-radius: 999px;
                        background: linear-gradient(90deg, #2563eb, #60a5fa);
                        box-shadow: 0 0 7px rgba(37,99,235,.28);
                      }

                      .stayx-premium-topnav .cm-channel-nav-item > a.active svg {
                        color: #175cd3;
                        stroke-width: 2.1;
                      }

                      .stayx-premium-topnav .cm-channel-nav-secondary > a:not(.active) {
                        color: #3e516d;
                      }

                      .stayx-premium-topnav .cm-channel-nav-more > a:not(.active) {
                        color: #52647f;
                        background: rgba(248,250,252,.86);
                        border-color: #e4e9f0;
                        box-shadow: none;
                      }

                      .stayx-premium-topnav .cm-channel-nav-more > a:not(.active)::before {
                        background: #f6f8fb;
                      }

                      .stayx-premium-topnav .cm-channel-nav-divider {
                        width: 1px;
                        height: 24px;
                        margin: 0 4px;
                        background: linear-gradient(
                          180deg,
                          transparent,
                          #ccd6e3 18%,
                          #ccd6e3 82%,
                          transparent
                        );
                        border: 0;
                        flex: 0 0 1px;
                      }

                      .stayx-premium-topnav .cm-channel-nav-item > a:focus-visible {
                        outline: none;
                        border-color: #8fb2f5;
                        box-shadow:
                          0 0 0 3px rgba(37,99,235,.10),
                          0 5px 14px rgba(37,99,235,.08);
                      }

                      @media (max-width: 1180px) {
                        .stayx-premium-topnav {
                          padding-left: 6px;
                          padding-right: 6px;
                        }

                        .stayx-premium-topnav .cm-channel-topnav-list {
                          gap: 2px;
                        }

                        .stayx-premium-topnav .cm-channel-nav-item > a {
                          padding-left: 7px;
                          padding-right: 8px;
                          gap: 6px;
                          font-size: 11.7px;
                        }

                        .stayx-premium-topnav .cm-channel-nav-divider {
                          margin-left: 2px;
                          margin-right: 2px;
                        }
                      }
                    `}</style>

                    <nav
                      className="submenu-horizontal cm-channel-topnav stayx-premium-topnav"
                      aria-label="StayX PMS navigation"
                    >
                      <ul className="cm-channel-topnav-list">
                        <li className="cm-channel-nav-item cm-channel-nav-primary">
                          <Link
                            className={isActive(["/hospitality"]) ? "active" : ""}
                            aria-current={isActive(["/hospitality"]) ? "page" : undefined}
                            to="/hospitality"
                            title={t("StayX Dashboard")}
                          >
                            <Home size={16} aria-hidden="true" />
                            <span>{t("Dashboard")}</span>
                          </Link>
                        </li>

                        <li className="cm-channel-nav-item cm-channel-nav-primary">
                          <Link
                            className={isSectionActive([
                              "/hospitality/reservation-list",
                              "/hospitality/reservation-details",
                              "/hospitality/guest-reservation",
                            ]) ? "active" : ""}
                            aria-current={isSectionActive([
                              "/hospitality/reservation-list",
                              "/hospitality/reservation-details",
                              "/hospitality/guest-reservation",
                            ]) ? "page" : undefined}
                            to="/hospitality/reservation-list"
                            title={t("Reservations")}
                          >
                            <BookOpen size={16} aria-hidden="true" />
                            <span>{t("Reservations")}</span>
                          </Link>
                        </li>

                        <li className="cm-channel-nav-item cm-channel-nav-primary">
                          <Link
                            className={isSectionActive(["/hospitality/calandar"]) ? "active" : ""}
                            aria-current={isSectionActive(["/hospitality/calandar"]) ? "page" : undefined}
                            to="/hospitality/calandar"
                            title={t("Calendar")}
                          >
                            <Calendar size={16} aria-hidden="true" />
                            <span>{t("Calendar")}</span>
                          </Link>
                        </li>

                        <li className="cm-channel-nav-item cm-channel-nav-primary">
                          <Link
                            className={isSectionActive(["/hospitality/guest-list-card"]) ? "active" : ""}
                            aria-current={isSectionActive(["/hospitality/guest-list-card"]) ? "page" : undefined}
                            to="/hospitality/guest-list-card"
                            title={t("Today's Arrivals")}
                          >
                            <LogIn size={16} aria-hidden="true" />
                            <span>{t("Today's Arrivals")}</span>
                          </Link>
                        </li>

                        <li className="cm-channel-nav-item cm-channel-nav-primary">
                          <Link
                            className={isSectionActive(["/hospitality/list-depature-guest-card"]) ? "active" : ""}
                            aria-current={isSectionActive(["/hospitality/list-depature-guest-card"]) ? "page" : undefined}
                            to="/hospitality/list-depature-guest-card"
                            title={t("Today's Departures")}
                          >
                            <LogOut size={16} aria-hidden="true" />
                            <span>{t("Today's Departures")}</span>
                          </Link>
                        </li>

                        <li className="cm-channel-nav-divider" aria-hidden="true" />

                        <li className="cm-channel-nav-item cm-channel-nav-secondary">
                          <Link
                            className={isSectionActive(["/hospitality/room-overview"]) ? "active" : ""}
                            aria-current={isSectionActive(["/hospitality/room-overview"]) ? "page" : undefined}
                            to="/hospitality/room-overview"
                            title={t("Rooms Overview")}
                          >
                            <Grid size={16} aria-hidden="true" />
                            <span>{t("Rooms")}</span>
                          </Link>
                        </li>

                        <li className="cm-channel-nav-item cm-channel-nav-secondary">
                          <Link
                            className={isSectionActive(["/hospitality/inhouse-guest"]) ? "active" : ""}
                            aria-current={isSectionActive(["/hospitality/inhouse-guest"]) ? "page" : undefined}
                            to="/hospitality/inhouse-guest"
                            title={t("In-House Guests")}
                          >
                            <Users size={16} aria-hidden="true" />
                            <span>{t("In-House")}</span>
                          </Link>
                        </li>

                        <li className="cm-channel-nav-divider" aria-hidden="true" />

                        <li
                          ref={stayXMoreRef}
                          className="cm-channel-nav-item cm-channel-nav-secondary cm-channel-nav-more"
                          style={{ position: "relative" }}
                        >
                          <Link
                            to="#"
                            aria-haspopup="menu"
                            aria-expanded={isStayXMoreOpen}
                            className={isSectionActive([
                              "/hospitality/refunds",
                              "/hospitality/list-checkedout-guest-card",
                            ]) ? "active" : ""}
                            onClick={(event) => {
                              event.preventDefault();
                              setIsStayXMoreOpen((open) => !open);
                            }}
                            title={t("More StayX tools")}
                          >
                            <MoreHorizontal size={16} aria-hidden="true" />
                            <span>{t("More")}</span>
                            <ChevronDown
                              size={14}
                              aria-hidden="true"
                              style={{
                                marginLeft: 3,
                                transform: isStayXMoreOpen ? "rotate(180deg)" : "rotate(0deg)",
                                transition: "transform 160ms ease",
                              }}
                            />
                          </Link>

                          {isStayXMoreOpen && createPortal(
                            <div
                              ref={stayXMoreDropdownRef}
                              role="menu"
                              aria-label={t("More StayX tools")}
                              style={{
                                position: "fixed",
                                top: stayXMorePosition.top,
                                left: stayXMorePosition.left,
                                zIndex: 10000,
                                width: 292,
                                padding: 8,
                                background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
                                border: "1px solid #dfe7f1",
                                borderRadius: 16,
                                boxShadow: "0 22px 56px rgba(15, 23, 42, 0.16)",
                              }}
                            >
                              <div
                                style={{
                                  padding: "8px 10px 9px",
                                  marginBottom: 3,
                                  borderBottom: "1px solid #eef2f7",
                                  fontSize: 10,
                                  fontWeight: 800,
                                  letterSpacing: ".09em",
                                  textTransform: "uppercase",
                                  color: "#8290a4",
                                }}
                              >
                                {t("More StayX tools")}
                              </div>

                              {[
                                {
                                  route: "/hospitality/refunds",
                                  label: "Refunds & Reconciliation",
                                  Icon: DollarSign,
                                },
                                {
                                  route: "/hospitality/list-checkedout-guest-card",
                                  label: "Checked-out Guests",
                                  Icon: CheckCircle,
                                },
                              ].map(({ route, label, Icon }) => (
                                <Link
                                  key={route}
                                  role="menuitem"
                                  className={isSectionActive([route]) ? "active" : ""}
                                  to={route}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                    width: "100%",
                                    minHeight: 42,
                                    padding: "10px 11px",
                                    borderRadius: 10,
                                    whiteSpace: "nowrap",
                                    fontSize: 12.5,
                                    fontWeight: 700,
                                  }}
                                >
                                  <Icon size={15} aria-hidden="true" />
                                  <span>{t(label)}</span>
                                </Link>
                              ))}
                            </div>,
                            document.body
                          )}
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
                {isSideMenu === "leaves" && (
                  <div className="tab-pane fade active show">
                    <div className="submenu-horizontal">
                      <ul>
                        <li>
                          <Link
                            className={isActive(['/leaves-employee']) ? "active" : ""}
                            to="/leaves-employee"
                          >
                            <FileMinus size={16} className="icon-with-margin" /> {t("My Leaves")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={isActive(['/adminleaves']) ? "active" : ""}
                            to="/adminleaves"
                          >
                            <FilePlus size={16} className="icon-with-margin" /> {t("Leave Admin")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={isActive(['/leave-applications']) ? "active" : ""}
                            to="/leave-applications"
                          >
                            <List size={16} className="icon-with-margin" /> {t("Leave Requests")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={isActive(['/leaves-balance']) ? "active" : ""}
                            to="/leaves-balance"
                          >
                            <Activity size={16} className="icon-with-margin" /> {t("Leave Balances")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={isActive(['/leave-settings']) ? "active" : ""}
                            to="/leave-settings"
                          >
                            <Settings size={16} className="icon-with-margin" /> {t("Settings")}
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
                {isSideMenu === "attendance" && (
                  <div className="tab-pane fade active show">
                    <div className="submenu-horizontal">
                      <ul>
                        <li>
                          <Link
                            className={isActive(['/attendance-weekly']) ? "active" : ""}
                            to="/attendance-weekly"
                          >
                            <Clipboard size={16} className="icon-with-margin" /> {t("My Attendance")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={isActive(['/attendance-daily']) ? "active" : ""}
                            to="/attendance-daily"
                          >
                            <Calendar size={16} className="icon-with-margin" /> {t("Daily Attendance")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={isActive(['/adminattendance']) ? "active" : ""}
                            to="/adminattendance"
                          >
                            <BarChart2 size={16} className="icon-with-margin" /> {t("Monthly Summary")}
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
                {isSideMenu === "devices" && (
                  <div className="tab-pane fade active show">
                    <div className="submenu-horizontal">
                      <ul>
                        <li>
                          <Link
                            className={isActive(['/devices']) ? "active" : ""}
                            to="/devices"
                          >
                            <Camera size={14} style={{ marginRight: '4px' }} /> {t("Manage AI Devices")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={isActive(['/deviceslog']) ? "active" : ""}
                            to="/deviceslog"
                          >
                            <FileText size={14} style={{ marginRight: '4px' }} /> {t("Device Logs")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={isActive(['/device-users']) ? "active" : ""}
                            to="/device-users"
                          >
                            <RefreshCw size={14} style={{ marginRight: '4px' }} /> {t("Synch Users")}
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
                {isSideMenu === "company-settings" && (
                  <div className="tab-pane fade active show">
                    <div className="submenu-horizontal">
                      <ul>
                        <li>
                          <Link
                            className={isActive(['/company-settings']) ? "active" : ""}
                            to="/company-settings"
                          >
                            <Home size={14} style={{ marginRight: '4px' }} /> {t("My Dashboard")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={isActive(['/manage-locations']) ? "active" : ""}
                            to="/manage-locations"
                          >
                            <MapPin size={14} style={{ marginRight: '4px' }} /> {t("Manage Locations")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={isActive(['/company-settings']) ? "active" : ""}
                            to="/company-settings"
                          >
                            <Settings size={14} style={{ marginRight: '4px' }} /> {t("Organization Settings")}
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
                {isSideMenu === "payroll-overview" && (
                  <div className="tab-pane fade active show">
                    <div className="submenu-horizontal">
                      <ul>
                        <li>
                          <Link
                            className={isActive(['/payroll-overview']) ? "active" : ""}
                            to="/payroll-overview"
                          >
                            <Clipboard size={16} className="icon-with-margin" /> {t("Payroll Overview")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={isActive(['/salary']) ? "active" : ""}
                            to="/salary"
                          >
                            ₹ {t("Employee Salary")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={isActive(['/payroll-items']) ? "active" : ""}
                            to="/payroll-items"
                          >
                            <FileMinus size={16} className="icon-with-margin" /> {t("Salary Heads")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={isActive(['/run-payroll']) ? "active" : ""}
                            to="/run-payroll"
                          >
                            <FileText size={16} className="icon-with-margin" /> {t("Process Payroll")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="/payroll-history"
                            className={isActive(['/payroll-history']) ? "active" : ""}
                          >
                            <Users size={16} className="icon-with-margin" /> {t("Payroll History")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className={isActive(['/payroll-settings']) ? "active" : ""}
                            to="/payroll-settings"
                          >
                            <Settings size={16} className="icon-with-margin" /> {t("Settings")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="/payroll-reports"
                            className={isActive(['/payroll-reports']) ? "active" : ""}
                          >
                            <BarChart2 size={16} className="icon-with-margin" /> {t("Reports")}
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
                {isSideMenu === "reports" && (
                  <div className="tab-pane fade active show">
                    <div className="submenu-horizontal">
                      <ul>
                        <li><Link to="/employee-reports">{t("Employee Reports")}</Link></li>
                        <li><Link to="/leave-reports">{t("Leave Reports")}</Link></li>
                        <li><Link to="/attendance-reports">{t("Attendance Reports")}</Link></li>
                        <li><Link to="/performance-reports">{t("Performance Reports")}</Link></li>
                      </ul>
                    </div>
                  </div>
                )}
                {isSideMenu === "settings" && (
                  <div className="tab-pane fade active show">
                    <div className="submenu-horizontal">
                      <ul>
                        <li><Link to="/hospitality/property-configuration">{t("Account Settings")}</Link></li>
                        <li><Link to="/privacy-settings">{t("Privacy Settings")}</Link></li>
                        <li><Link to="/notification-settings">{t("Notification Settings")}</Link></li>
                        <li><Link to="/theme-settings">{t("Theme Settings")}</Link></li>
                      </ul>
                    </div>
                  </div>
                )}
                {isSideMenu === "profile" && (
                  <div className="tab-pane fade active show">
                    <div className="submenu-horizontal d-flex justify-content-between align-items-center">
                      <ul>
                        <li>
                          <Link to="/employees">
                            <ChevronLeft size={16} /> {t("Employee Directory")}
                          </Link>
                        </li>
                      </ul>
                      <ul className="nav nav-tabs-bottom">
                        <li>
                          <Link
                            to="#emp_profile"
                            data-bs-toggle="tab"
                            aria-selected="true"
                            className="active"
                            ref={profileTabRef}
                          >
                            <User size={16} /> {t("Personal Profile")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="#emp_details"
                            data-bs-toggle="tab"
                          >
                            <Briefcase size={16} /> {t("Employment Details")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="#emp_assets"
                            data-bs-toggle="tab"
                          >
                            <Box size={16} /> {t("Assets")}
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Chatbot isOpen={isChatbotOpen} toggleChatbot={toggleChatbot} />
    </>
  );
};

export default Sidebar;